import { useEffect, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  CircleOff,
  Download,
  Link2,
  MessageCircle,
  QrCode,
  Unplug
} from "lucide-react";

import { protectedWhatsAppFetch } from "../../services/protectedWhatsApp";
import {
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  importGoogleCalendar
} from "../../services/googleCalendarAccess";
import { doc, getDoc, onSnapshot } from "../../access/firestore";
import { db } from "../../firebase";
import { useClinic } from "../../context/ClinicContext";
import { useNotifications } from "../../notifications/NotificationProvider";
import { requestVexaConfirm } from "../../notifications/dialogService";

function ConnectionStatus({ connected }) {
  return (
    <span className={`vd-connection-status${connected ? " is-connected" : ""}`}>
      {connected ? <CheckCircle2 size={14} /> : <CircleOff size={14} />}
      {connected ? "Conectado" : "No conectado"}
    </span>
  );
}

function ConnectionCard({ icon: Icon, tone, title, description, connected, children }) {
  return (
    <section className="vd-connection-card">
      <div className="vd-connection-card-head">
        <div className={`vd-connection-icon is-${tone}`}><Icon size={21} /></div>
        <div className="vd-connection-copy">
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <ConnectionStatus connected={connected} />
      </div>
      <div className="vd-connection-card-body">{children}</div>
    </section>
  );
}

export default function ConnectionsSettings() {
  const { clinicId } = useClinic();
  const { notify } = useNotifications();
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false, email: "" });
  const [connectingWhatsApp, setConnectingWhatsApp] = useState(false);
  const [importingGoogle, setImportingGoogle] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [qrRequested, setQrRequested] = useState(false);

  useEffect(() => {
    if (!clinicId) return undefined;
    return onSnapshot(doc(db, "clinics", clinicId), snapshot => {
      setWhatsappStatus(snapshot.data()?.whatsapp || null);
    });
  }, [clinicId]);

  useEffect(() => {
    if (!clinicId) return undefined;
    let active = true;
    getDoc(doc(db, "clinics", clinicId, "integrationStatus", "googleCalendar"))
      .then(snapshot => {
        if (!active) return;
        const data = snapshot.exists() ? snapshot.data() : {};
        setGoogleCalendarStatus({ connected: data.connected === true, email: data.email || "" });
      })
      .catch(error => console.error("Error cargando Google Calendar:", error));
    return () => { active = false; };
  }, [clinicId]);

  const connectWhatsApp = async () => {
    if (!clinicId) return;
    try {
      setConnectingWhatsApp(true);
      const response = await protectedWhatsAppFetch("https://wa.vexadent.com/create-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clinicId })
      });
      if (!response.ok) throw new Error(`No fue posible iniciar la conexión (${response.status}).`);
      setQrRequested(true);
      notify({ source: "vexa-alert", tone: "success", title: "WhatsApp", message: "La sesión se está preparando. El código QR aparecerá aquí en unos segundos." });
    } catch (error) {
      notify({ source: "vexa-alert", tone: "error", title: "WhatsApp", message: error.message || "No fue posible iniciar la conexión." });
    } finally {
      setConnectingWhatsApp(false);
    }
  };

  const disconnectWhatsApp = async () => {
    if (!clinicId || !(await requestVexaConfirm("¿Cerrar sesión de WhatsApp?"))) return;
    try {
      const response = await protectedWhatsAppFetch("https://wa.vexadent.com/disconnect-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clinicId })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "No fue posible desconectar WhatsApp.");
      setQrRequested(false);
      notify({ source: "vexa-alert", tone: "success", title: "WhatsApp", message: "WhatsApp fue desconectado." });
    } catch (error) {
      notify({ source: "vexa-alert", tone: "error", title: "WhatsApp", message: error.message || "Error al desconectar WhatsApp." });
    }
  };

  const connectGoogle = async () => {
    try {
      await connectGoogleCalendar(clinicId);
    } catch (error) {
      notify({ source: "vexa-alert", tone: "error", title: "Google Calendar", message: error.message || "No fue posible conectar Google Calendar." });
    }
  };

  const disconnectGoogle = async () => {
    if (!clinicId || !(await requestVexaConfirm("¿Desconectar Google Calendar?"))) return;
    try {
      await disconnectGoogleCalendar(clinicId);
      setGoogleCalendarStatus({ connected: false, email: "" });
      setImportResult(null);
      notify({ source: "vexa-alert", tone: "success", title: "Google Calendar", message: "Google Calendar fue desconectado." });
    } catch (error) {
      notify({ source: "vexa-alert", tone: "error", title: "Google Calendar", message: error.message || "No fue posible desconectar Google Calendar." });
    }
  };

  const importGoogle = async () => {
    if (!clinicId) return;
    try {
      setImportingGoogle(true);
      setImportResult(null);
      const result = await importGoogleCalendar(clinicId);
      if (!result.success) throw new Error(result.message || "No fue posible importar eventos.");
      setImportResult(result);
      notify({ source: "vexa-alert", tone: "success", title: "Google Calendar", message: "Importación finalizada." });
    } catch (error) {
      notify({ source: "vexa-alert", tone: "error", title: "Google Calendar", message: error.message || "Error al importar eventos." });
    } finally {
      setImportingGoogle(false);
    }
  };

  const whatsappConnected = whatsappStatus?.connected === true;
  const googleConnected = googleCalendarStatus.connected === true;
  const activeConnections = Number(whatsappConnected) + Number(googleConnected);
  const waitingForQr = !whatsappConnected && (qrRequested || whatsappStatus?.status === "waiting_qr");

  return (
    <div className="vd-connections-page">
      <div className="vd-connections-intro">
        <div>
          <p>Servicios conectados</p>
          <h2>Conecta solo lo que necesitas.</h2>
          <span>Administra WhatsApp y Google Calendar desde dos tarjetas claras.</span>
        </div>
        <div className="vd-connections-summary"><span className={activeConnections > 0 ? "is-on" : ""} /> {activeConnections === 1 ? "1 conexión activa" : activeConnections > 1 ? `${activeConnections} conexiones activas` : "Sin conexiones activas"}</div>
      </div>

      <div className="vd-connections-grid">
        <ConnectionCard icon={MessageCircle} tone="green" title="WhatsApp" description="Recordatorios, confirmaciones y conversaciones con pacientes." connected={whatsappConnected}>
          {whatsappConnected && whatsappStatus.phoneNumber && <div className="vd-connection-detail"><strong>Número conectado</strong><span>{whatsappStatus.phoneNumber}</span></div>}
          {!whatsappConnected && waitingForQr && whatsappStatus?.qr && <div className="vd-connection-qr"><img src={whatsappStatus.qr} alt="Código QR de WhatsApp" /><span>Escanéalo desde WhatsApp Business.</span></div>}
          {!whatsappConnected && waitingForQr && !whatsappStatus?.qr && <div className="vd-connection-pending"><QrCode size={17} /><span>Estamos preparando el código QR. Mantén esta pantalla abierta.</span></div>}
          <div className="vd-connection-actions">
            {!whatsappConnected ? (
              <button type="button" className="vd-connection-primary is-green" onClick={connectWhatsApp} disabled={connectingWhatsApp}><Link2 size={16} />{connectingWhatsApp ? "Conectando..." : "Conectar WhatsApp"}</button>
            ) : (
              <>
                <button type="button" className="vd-connection-primary is-green" onClick={connectWhatsApp} disabled={connectingWhatsApp}><Link2 size={16} />Reconectar</button>
                <button type="button" className="vd-connection-secondary is-danger" onClick={disconnectWhatsApp}><Unplug size={16} />Desconectar</button>
              </>
            )}
          </div>
        </ConnectionCard>

        <ConnectionCard icon={CalendarDays} tone="blue" title="Google Calendar" description="Consulta e importa tus eventos junto con la agenda de VexaDent." connected={googleConnected}>
          {googleConnected && googleCalendarStatus.email && <div className="vd-connection-detail"><strong>Cuenta conectada</strong><span>{googleCalendarStatus.email}</span></div>}
          {importResult && <div className="vd-connection-import-result"><strong>Importación completada</strong><span>{importResult.imported || 0} importados · {importResult.skipped || 0} omitidos · {importResult.total || 0} encontrados</span></div>}
          <div className="vd-connection-actions">
            {!googleConnected ? (
              <button type="button" className="vd-connection-primary is-blue" onClick={connectGoogle}><Link2 size={16} />Conectar Google Calendar</button>
            ) : (
              <>
                <button type="button" className="vd-connection-primary is-blue" onClick={importGoogle} disabled={importingGoogle}><Download size={16} />{importingGoogle ? "Importando..." : "Importar eventos"}</button>
                <button type="button" className="vd-connection-secondary is-danger" onClick={disconnectGoogle}><Unplug size={16} />Desconectar</button>
              </>
            )}
          </div>
        </ConnectionCard>
      </div>

      <div className="vd-connections-note"><CheckCircle2 size={16} /><span>Las automatizaciones, plantillas y horarios de envío se configuran en “Campañas y plantillas”. Aquí solo se administran las conexiones.</span></div>
    </div>
  );
}
